import { useMemo, useRef, useState } from "react";
import { searchCatalog, type SearchableTrack } from "../game/search.ts";

/**
 * The Dropdown. Submit is impossible until a real Catalog Track is selected,
 * which is what makes every Verdict exact.
 */
export function GuessInput({
  catalog,
  disabled,
  onGuess,
  onSkip,
}: {
  catalog: SearchableTrack[];
  disabled: boolean;
  onGuess: (track: SearchableTrack) => void;
  onSkip: () => void;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<SearchableTrack | null>(null);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * The song list arrives after the audio does — see the two fetches in App.
   *
   * A Guess must be a real Catalog Track, so without the list there is nothing to
   * pick and the field would silently do nothing. Saying so is better than an
   * input that ignores typing.
   */
  const listLoading = catalog.length === 0;

  const results = useMemo(
    () => (selected ? [] : searchCatalog(catalog, query)),
    [catalog, query, selected],
  );

  function choose(track: SearchableTrack) {
    setSelected(track);
    setQuery(`${track.artist} — ${track.title}`);
    inputRef.current?.blur();
  }

  function reset() {
    setSelected(null);
    setQuery("");
    setHighlight(0);
  }

  function submit() {
    if (!selected || disabled) return;
    onGuess(selected);
    reset();
  }

  return (
    <div className="guess">
      <div className="guess__field">
        <input
          ref={inputRef}
          className="guess__input"
          type="text"
          value={query}
          disabled={disabled || listLoading}
          placeholder={listLoading ? "Loading song list…" : "Search for a song"}
          autoComplete="off"
          aria-label="Search for a song"
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(null);
            setHighlight(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, results.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const pick = results[highlight];
              if (pick) choose(pick);
              else if (selected) submit();
            } else if (e.key === "Escape") {
              reset();
            }
          }}
        />
        {query && (
          <button className="guess__clear" onClick={reset} aria-label="Clear" type="button">
            ×
          </button>
        )}
      </div>

      {results.length > 0 && (
        <ul className="dropdown" role="listbox">
          {results.map((track, i) => (
            <li key={track.id}>
              <button
                type="button"
                role="option"
                aria-selected={i === highlight}
                className={`dropdown__item${i === highlight ? " dropdown__item--on" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(track);
                }}
              >
                <span className="dropdown__title">{track.title}</span>
                <span className="dropdown__artist">{track.artist}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="guess__actions">
        <button className="btn btn--ghost" onClick={onSkip} disabled={disabled} type="button">
          Skip
        </button>
        <button className="btn btn--primary" onClick={submit} disabled={disabled || !selected} type="button">
          Guess
        </button>
      </div>
    </div>
  );
}
